import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { toast } from "@/components/toast/toast-queue";

import type { SourceFileRow } from "../hooks/useSourceFileUploads";
import type { FileResult } from "../types";
import type { FileVersionMetadata } from "../types/fileVersionMetadata";
import type { SessionFileRow } from "../types/sessionFile";

import { useClientDeliveryMessage } from "../hooks/useClientDeliveryMessage";
import { useConfidenceCheckerSession } from "../hooks/useConfidenceCheckerSession";
import { useSourceFileUploads } from "../hooks/useSourceFileUploads";
import { isFileResultCleared } from "../types";
import { getMentionedIdsFromHtml } from "../utils/getMentionedIdsFromHtml";
import {
  isSessionFileAttached,
  isSessionFileTransferring,
} from "../utils/reconcileInFlightUploads";

export interface DesignConfidenceCheckerSessionContext {
  activeSessionQuery: ReturnType<
    typeof useConfidenceCheckerSession
  >["activeSessionQuery"];
  addFiles: (files: File[]) => void;
  cancelUpload: (clientId: string) => void;
  discardAndClose: (onDone: () => void) => Promise<void>;
  displayResults: FileResult[];
  hasResolvedResume: boolean;
  isSubmitting: boolean;
  override: (evaluationId: number) => void;
  removeFile: (file: SessionFileRow) => void;
  replaceFile: (input: { attachmentId: number; file: File }) => void;
  results: FileResult[];
  resumeSession: () => Promise<void>;
  sessionFiles: SessionFileRow[];
  sessionId: null | string;
  startFresh: () => Promise<void>;
  submit: (options: {
    mentionedUserIds?: number[];
    message?: string;
    postOnActivityLog: boolean;
  }) => Promise<string>;
  updateFileMetadata: (
    clientId: string,
    patch: Partial<FileVersionMetadata>,
  ) => void;
}

export interface DesignConfidenceCheckerSourceUploadsContext {
  addFiles: (files: File[]) => void;
  attachToRequest: () => Promise<void>;
  isUploading: boolean;
  removeFile: (clientId: string) => void;
  reset: () => void;
  sourceFiles: SourceFileRow[];
  updateFileMetadata: (clientId: string, meta: FileVersionMetadata) => void;
}

export interface DesignConfidenceCheckerDeliveryMessageContext {
  isSendToChat: boolean;
  message: string;
  setIsSendToChat: (value: boolean) => void;
  setMessage: (value: string) => void;
}

export interface DesignConfidenceCheckerShellContext {
  allFilesCleared: boolean;
  attachedFileCount: number;
  filesForUpload: File[];
  handleCloseOnly: () => void;
  handleCloseRequest: () => void;
  handleSubmit: () => Promise<void>;
  hasLoadingEvaluations: boolean;
  isAwaitingFirstSnapshot: boolean;
  isCheckingDisabled: boolean;
  isContinueDisabled: boolean;
  isFinalizing: boolean;
  isResolvingSession: boolean;
  isResultsInitialLoading: boolean;
  isSessionUploading: boolean;
  isSubmitting: boolean;
  showResumePrompt: boolean;
}

export type DesignConfidenceCheckerContextValue = {
  deliveryMessage: DesignConfidenceCheckerDeliveryMessageContext;
  requestId: number;
  session: DesignConfidenceCheckerSessionContext;
  shell: DesignConfidenceCheckerShellContext;
  sourceUploads: DesignConfidenceCheckerSourceUploadsContext;
};

const DesignConfidenceCheckerContext =
  createContext<DesignConfidenceCheckerContextValue | null>(null);

export function useDcc(): DesignConfidenceCheckerContextValue {
  const ctx = useContext(DesignConfidenceCheckerContext);

  if (!ctx) {
    throw new Error(
      "useDcc must be used within DesignConfidenceCheckerProvider",
    );
  }

  return ctx;
}

interface DesignConfidenceCheckerProviderProps {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: (sessionId: string) => void;
  requestId: number;
}

export function DesignConfidenceCheckerProvider({
  children,
  isOpen,
  onClose,
  onSubmitted,
  requestId,
}: DesignConfidenceCheckerProviderProps) {
  const session = useConfidenceCheckerSession({ isOpen, requestId });
  const deliveryMessage = useClientDeliveryMessage(requestId, isOpen);
  const sourceUploads = useSourceFileUploads(requestId);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const { reset: resetSourceUploads } = sourceUploads;

  useEffect(() => {
    if (!isOpen) {
      resetSourceUploads();
    }
  }, [isOpen, resetSourceUploads]);

  const filesForUpload = useMemo(
    () =>
      session.sessionFiles
        .filter((file) => isSessionFileAttached(file))
        .map((file) => file.file),
    [session.sessionFiles],
  );

  const attachedFileCount = session.sessionFiles.filter((file) =>
    isSessionFileAttached(file),
  ).length;

  const isSessionUploading = session.sessionFiles.some((file) =>
    isSessionFileTransferring(file),
  );

  const allFilesCleared =
    session.results.length > 0 &&
    session.results.every((result) => isFileResultCleared(result));

  const hasLoadingEvaluations = session.results.some(
    (result) => result.status === "loading",
  );

  const isAwaitingFirstSnapshot =
    session.sessionId !== null &&
    session.snapshotQuery.isLoading &&
    session.results.length === 0;

  const isResultsInitialLoading =
    isAwaitingFirstSnapshot ||
    (session.results.length > 0 &&
      session.results.every((result) => result.status === "loading"));

  const isCheckingDisabled = attachedFileCount === 0 || isSessionUploading;
  const isContinueDisabled =
    hasLoadingEvaluations || !allFilesCleared || isSessionUploading;
  const isSubmitting = session.isSubmitting || isFinalizing;

  const activeServerSession = session.activeSessionQuery.data?.session ?? null;
  const isAwaitingResumeDecision =
    !session.hasResolvedResume && session.sessionId === null;
  const isResolvingSession =
    isOpen && isAwaitingResumeDecision && session.activeSessionQuery.isLoading;
  const showResumePrompt =
    activeServerSession !== null && isAwaitingResumeDecision;

  const handleCloseOnly = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleCloseRequest = useCallback(() => {
    if (session.sessionId === null && session.sessionFiles.length === 0) {
      onClose();

      return;
    }
    void session.discardAndClose(onClose);
  }, [onClose, session]);

  const handleSubmit = useCallback(async () => {
    setIsFinalizing(true);
    try {
      const mentionedUserIds = getMentionedIdsFromHtml(deliveryMessage.message);

      await sourceUploads.attachToRequest();

      const hasSession = session.sessionId !== null;

      if (hasSession) {
        const sessionId = await session.submit({
          mentionedUserIds:
            mentionedUserIds.length > 0 ? mentionedUserIds : undefined,
          message: deliveryMessage.message,
          postOnActivityLog: deliveryMessage.isSendToChat,
        });

        onSubmitted?.(sessionId);
      }

      onClose();
      toast.add({
        description: hasSession
          ? "Your files have been sent to the client."
          : "Your source files have been added to the request.",
        timeout: 5000,
        title: hasSession ? "Designs submitted" : "Source files uploaded",
        type: "success",
      });
    } catch {
      toast.add({
        description: "Something went wrong. Please try again.",
        title: "Submission failed",
        type: "error",
      });
    } finally {
      setIsFinalizing(false);
    }
  }, [deliveryMessage, onClose, onSubmitted, session, sourceUploads]);

  const sessionContext = useMemo<DesignConfidenceCheckerSessionContext>(
    () => ({
      activeSessionQuery: session.activeSessionQuery,
      addFiles: (files) => {
        void session.addFiles(files);
      },
      cancelUpload: session.cancelUpload,
      discardAndClose: session.discardAndClose,
      displayResults: session.displayResults,
      hasResolvedResume: session.hasResolvedResume,
      isSubmitting: session.isSubmitting,
      override: (evaluationId) => {
        void session.override(evaluationId);
      },
      removeFile: (file) => {
        void session.removeFile({
          attachmentId: file.attachmentId ?? undefined,
          clientId: file.clientId,
        });
      },
      replaceFile: session.replaceFile,
      results: session.results,
      resumeSession: session.resumeSession,
      sessionFiles: session.sessionFiles,
      sessionId: session.sessionId,
      startFresh: session.startFresh,
      submit: session.submit,
      updateFileMetadata: session.updateFileMetadata,
    }),
    [session],
  );

  const sourceUploadsContext =
    useMemo<DesignConfidenceCheckerSourceUploadsContext>(
      () => ({
        addFiles: sourceUploads.addFiles,
        attachToRequest: sourceUploads.attachToRequest,
        isUploading: sourceUploads.isUploading,
        removeFile: sourceUploads.removeFile,
        reset: sourceUploads.reset,
        sourceFiles: sourceUploads.sourceFiles,
        updateFileMetadata: sourceUploads.updateFileMetadata,
      }),
      [sourceUploads],
    );

  const deliveryMessageContext =
    useMemo<DesignConfidenceCheckerDeliveryMessageContext>(
      () => ({
        isSendToChat: deliveryMessage.isSendToChat,
        message: deliveryMessage.message,
        setIsSendToChat: deliveryMessage.setIsSendToChat,
        setMessage: deliveryMessage.setMessage,
      }),
      [deliveryMessage],
    );

  const shellContext = useMemo<DesignConfidenceCheckerShellContext>(
    () => ({
      allFilesCleared,
      attachedFileCount,
      filesForUpload,
      handleCloseOnly,
      handleCloseRequest,
      handleSubmit,
      hasLoadingEvaluations,
      isAwaitingFirstSnapshot,
      isCheckingDisabled,
      isContinueDisabled,
      isFinalizing,
      isResolvingSession,
      isResultsInitialLoading,
      isSessionUploading,
      isSubmitting,
      showResumePrompt,
    }),
    [
      allFilesCleared,
      attachedFileCount,
      filesForUpload,
      handleCloseOnly,
      handleCloseRequest,
      handleSubmit,
      hasLoadingEvaluations,
      isAwaitingFirstSnapshot,
      isCheckingDisabled,
      isContinueDisabled,
      isFinalizing,
      isResolvingSession,
      isResultsInitialLoading,
      isSessionUploading,
      isSubmitting,
      showResumePrompt,
    ],
  );

  const value = useMemo(
    () => ({
      deliveryMessage: deliveryMessageContext,
      requestId,
      session: sessionContext,
      shell: shellContext,
      sourceUploads: sourceUploadsContext,
    }),
    [
      deliveryMessageContext,
      requestId,
      sessionContext,
      shellContext,
      sourceUploadsContext,
    ],
  );

  return (
    <DesignConfidenceCheckerContext.Provider value={value}>
      {children}
    </DesignConfidenceCheckerContext.Provider>
  );
}
